<template>
  <div>
    <div class="text-center mb-5">
      <h1>Bienvenue à Dlibr</h1>
      <h3 class="text-primary">Création d'un compte</h3>
      <h2 class="lead h4 text-primary mb-3">
        Veuillez remplir le formulaire suivant.
      </h2>
    </div>
    <b-form @submit.stop.prevent="onSubmit" v-if="formVisible">
      <b-form-group id="name" label="Prénom" label-for="name-input">
        <b-form-input
          id="name-input"
          name="name-input"
          @change="setValue('name', $event)"
          :value="form.name"
          :state="validateState('name')"
          aria-describedby="input-name-live-feedback"
        />
        <b-form-invalid-feedback id="input-name-live-feedback"
          >Ce champ est requis.</b-form-invalid-feedback
        >
      </b-form-group>

      <b-form-group id="last_name" label="Nom" label-for="last_name-input">
        <b-form-input
          id="last_name-input"
          name="last_name-input"
          @change="setValue('last_name', $event)"
          :value="form.last_name"
          :state="validateState('last_name')"
          aria-describedby="input-last_name-live-feedback"
        />
        <b-form-invalid-feedback id="input-last_name-live-feedback"
          >Ce champ est requis.</b-form-invalid-feedback
        >
      </b-form-group>

      <b-form-group
        id="nickname"
        label="Nom d'utilisateur"
        label-for="nickname-input"
      >
        <b-form-input
          id="nickname-input"
          name="nickname-input"
          @change="setValue('nickname', $event)"
          :value="form.nickname"
          :state="validateState('nickname')"
          aria-describedby="input-nickname-live-feedback"
        />
        <b-form-invalid-feedback id="input-nickname-live-feedback"
          >Ce champ est requis.</b-form-invalid-feedback
        >
      </b-form-group>

      <b-form-group
        id="email"
        label="Adresse courriel "
        label-for="email-input"
      >
        <b-form-input
          id="email-input"
          name="email-input"
          @change="setValue('email', $event)"
          :value="form.email"
          :state="validateState('email')"
          aria-describedby="input-email-live-feedback"
        />
        <b-form-invalid-feedback id="input-email-live-feedback"
          >Ce champ est requis et doit-être un courriel
          valid.</b-form-invalid-feedback
        >
      </b-form-group>

      <div class="d-flex justify-content-end align-items-center">
        <b-form-checkbox
          size="sm"
          id="signedContract"
          v-model="form.signedContract"
          name="signedContract"
          class="pr-3"
        >
          J'accepte les
          <b-link target="_blank" :to="{ name: 'auth.conditions' }"
            >conditions d'utilisation.</b-link
          >
        </b-form-checkbox>
        <b-btn
          pill
          :disabled="registerStatus == 'fetching' || !form.signedContract"
          class="px-4"
          type="submit"
          variant="primary"
        >
          <span>Créer un compte</span>
          <b-spinner class="ml-2" small v-if="registerStatus == 'fetching'" />
        </b-btn>
      </div>
    </b-form>
    <div
      class="py-5 text-center animate__animated animate__bounceInDown"
      v-if="!formVisible"
    >
      <b-jumbotron bg-variant="light">
        <h3>
          Merci, vous recevrez un courriel afin de terminer votre inscription.
        </h3>
        <b-icon icon="mailbox" font-scale="3" animation="fade"></b-icon>
      </b-jumbotron>
    </div>
  </div>
</template>

<script>
/**
 * @file       Register component.
 * @author     Appwapp <info@appwapp.com>
 * @copyright  © Centre collégial de développement de matériel didactique, Collège de Maisonneuve, 2021
 * @license    https://www.ccdmd.qc.ca
 * @version    1.0
 */
import { required, email } from "vuelidate/lib/validators";
import { mapActions, mapState } from "vuex";

/**
 * Export component.
 * 
 * @type  {Object}
 */
export default {
  /**
   * Data used in the component.
   *
   * @return  {Object}
   */
  data() {
    return {
      formVisible: true,
      form: {
        name: null,
        last_name: null,
        nickname: null,
        email: null,
        signedContract: null,
        role: "student"
      }
    };
  },

  /**
   * Vuelidate form validations.
   * 
   * @type  {Object}
   */
  validations: {
    form: {
      name: { required },
      last_name: { required },
      nickname: { required },
      email: {
        required,
        email
      },
      signedContract: { required },
      role: {}
    }
  },

  /**
   * Computed properties.
   * 
   * @type  {Object}
   */
  computed: {
    ...mapState({
      registerStatus: state => state.students.register.status
    })
  },

  /**
   * Methods.
   * 
   * @type  {Object}
   */
  methods: {
    ...mapActions("students", {
      register: "register"
    }),

    /**
     * Sets the form value.
     *
     * @param      {string}  key     The new value
     * @param      {string}  value   The value
     */
    setValue(key, value) {
      this.form[key] = value;
      this.$v.form[key].$touch();
    },

    /**
     * Validate the form state.
     *
     * @param      {string}  name    The input name
     *
     * @return     {bool}  Dirty or not.
     */
    validateState(name) {
      const { $dirty, $error } = this.$v.form[name];
      return $dirty ? !$error : null;
    },

    /**
     * Resets the form.
     */
    resetForm() {
      this.form = {
        email: null,
        password: null
      };

      // Reset on next tick
      this.$nextTick(() => {
        this.$v.$reset();
      });
    },

    /**
     * Called on submit.
     */
    onSubmit() {
      this.$v.form.$touch();

      // If any error found, stop the submit
      if (this.$v.form.$anyError) {
        return;
      }

      // Sends the register request
      this.register(this.form)
        .then(() => {
          this.formVisible = false;

          // Display a success message
          this.$root.$bvToast.toast(
            "Veuillez vérifier vos courriel pour confirmer votre inscription.",
            {
              title: "Bienvenue à Dlibr."
            }
          );
        })
        .catch(err => {
          let error = "";

          // Display an error message
          switch (err.response.data.message) {
            case "Email duplicated": {
              error = "Cette adresse courriel est déjà utilisé";
              break;
            }
            case "The nickname has already been taken": {
              error = "Ce pseudonyme est déjà utilisé, veuillez en choisir un autre.";
              break;
            }
            default: {
              error = err.response.data.message;
              break;
            }
          }

          this.$root.$bvToast.toast(error, {
            title:   "Erreur",
            variant: "danger"
          });
        });
    }
  }
};
</script>
<style lang="scss" scoped>
.jumbotron {
  border-radius: 15px !important;
}
</style>
